import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, HelpCircle, Search, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { api, getMunicipioSlug } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  emojiFor,
  fetchCategoriasOficiais,
  placeholderFor,
} from '../utils/solicitacoesHelpers';
import useMunicipioFeatures from '../hooks/useMunicipioFeatures';

/**
 * Solicitar ajuda — fluxo do consumidor.
 *
 * Etapas:
 *  0) Aviso obrigatório "não para urgências" (sem botão pular)
 *  1) Selecionar categoria (grid 24 categorias)
 *  2) Descrever a necessidade (textarea 200 chars + WhatsApp)
 *  3) Confirmar e enviar
 */
export default function SolicitarAjuda() {
  const { user, openLoginModal } = useAuth() || {};
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const slug = getMunicipioSlug();
  const { preciso_de_enabled: precisoDeEnabled } = useMunicipioFeatures(slug);

  // Enforcement: se a prefeitura desativou "Preciso de…", redireciona para o
  // home do município (não permitir acesso via URL direta).
  useEffect(() => {
    if (precisoDeEnabled === false) {
      navigate(slug ? `/${slug}` : '/', { replace: true });
    }
  }, [precisoDeEnabled, slug, navigate]);

  const [step, setStep] = useState(0); // 0 = aviso
  const [categorias, setCategorias] = useState([]);
  const [categoria, setCategoria] = useState(search.get('categoria') || '');
  const [texto, setTexto] = useState('');
  const [whatsapp, setWhatsapp] = useState(user?.whatsapp || user?.telefone || '');
  const [submitting, setSubmitting] = useState(false);
  const [abertas, setAbertas] = useState([]);

  useEffect(() => {
    if (!user) {
      if (openLoginModal) openLoginModal();
      return;
    }
    fetchCategoriasOficiais().then(setCategorias);
    // carrega minhas abertas para checar limite
    api.get('/v2/solicitacoes/minhas')
      .then((r) => setAbertas((r.data || []).filter((s) => s.status === 'aberta')))
      .catch(() => setAbertas([]));
  }, [user, openLoginModal]);

  const limiteAtingido = useMemo(() => abertas.length >= 2, [abertas]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6" data-testid="solicitar-precisa-login">
        <div className="bg-white rounded-2xl p-6 text-center max-w-md w-full border">
          <h1 className="text-xl font-bold mb-2">Faça login para continuar</h1>
          <p className="text-sm text-gray-600 mb-4">Você precisa estar logado para abrir uma solicitação.</p>
          <Button onClick={openLoginModal} data-testid="solicitar-abrir-login">Entrar</Button>
        </div>
      </div>
    );
  }

  async function enviar() {
    if (!categoria) { toast.error('Escolha uma categoria'); return; }
    if (!texto.trim()) { toast.error('Descreva a sua necessidade'); return; }
    if (texto.length > 200) { toast.error('Texto excede 200 caracteres'); return; }
    if (!whatsapp.trim()) { toast.error('Informe um WhatsApp para contato'); return; }
    setSubmitting(true);
    try {
      await api.post('/v2/solicitacoes', {
        categoria,
        texto: texto.trim(),
        user_whatsapp: whatsapp.replace(/\D/g, ''),
      });
      toast.success(`Solicitação enviada! Os profissionais de ${categoria} serão notificados.`);
      navigate('/minhas-solicitacoes');
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || 'Erro ao enviar';
      toast.error(status === 429 ? detail : detail);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-32" data-testid="solicitar-page">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-purple-700 mb-3">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
        <h1 className="text-2xl font-bold flex items-center gap-2 mb-1">
          <HelpCircle className="w-6 h-6 text-purple-600" /> Preciso de…
        </h1>
        <p className="text-sm text-gray-500 mb-5">Conte o que você precisa e a cidade te ajuda a encontrar.</p>

        {limiteAtingido && step !== 0 && (
          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-4 flex items-start gap-2" data-testid="solicitar-limite">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold">Você já tem 2 solicitações abertas.</p>
              <p>Resolva ou aguarde expirar antes de abrir uma nova.</p>
              <Link to="/minhas-solicitacoes" className="text-purple-700 hover:underline inline-block mt-1">Ver minhas solicitações →</Link>
            </div>
          </div>
        )}

        {/* Step 0: aviso obrigatório */}
        {step === 0 && (
          <div className="bg-white rounded-2xl border-2 border-amber-200 p-5" data-testid="solicitar-aviso">
            <div className="flex items-start gap-2 mb-3">
              <div className="text-2xl">📋</div>
              <h2 className="text-lg font-bold">Como funciona</h2>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              Este canal é para <strong>agendamentos e demandas com prazo</strong> — não para urgências.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed mt-2">
              Para serviços urgentes, acesse o contato direto do profissional na busca da plataforma.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-5">
              <Button
                onClick={() => setStep(1)}
                disabled={limiteAtingido}
                data-testid="solicitar-entendi-btn"
                className="w-full"
              >
                Entendi, quero continuar
              </Button>
              <Button variant="outline" asChild className="w-full">
                <Link to={slug ? `/${slug}` : '/'} data-testid="solicitar-buscar-btn">
                  <Search className="w-4 h-4 mr-1" /> Buscar profissionais
                </Link>
              </Button>
            </div>
            {limiteAtingido && (
              <p className="text-xs text-amber-700 mt-3 text-center">Você já atingiu o limite de 2 solicitações abertas.</p>
            )}
          </div>
        )}

        {/* Stepper visual */}
        {step > 0 && (
          <ol className="flex items-center gap-2 mb-6 text-xs">
            {['Categoria', 'Descrição', 'Confirmar'].map((s, i) => (
              <li key={s} className="flex items-center gap-2">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center font-bold ${
                  step >= i + 1 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}>{i + 1}</span>
                <span className={step === i + 1 ? 'font-semibold text-purple-700' : 'text-gray-500'}>{s}</span>
                {i < 2 && <ArrowRight className="w-3 h-3 text-gray-400" />}
              </li>
            ))}
          </ol>
        )}

        {/* Step 1: categoria */}
        {step === 1 && (
          <div data-testid="solicitar-step-1">
            <Label className="mb-2 block">Qual categoria descreve melhor o que você precisa?</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {categorias.map((c) => (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => setCategoria(c.nome)}
                  data-testid={`solicitar-cat-${c.slug}`}
                  className={`text-left p-3 rounded-xl border transition ${
                    categoria === c.nome
                      ? 'border-purple-600 bg-purple-50 shadow-sm'
                      : 'border-gray-200 bg-white hover:border-purple-300'
                  }`}
                >
                  <div className="text-xl">{emojiFor(c.nome)}</div>
                  <div className="text-xs font-medium mt-1 truncate">{c.nome}</div>
                </button>
              ))}
            </div>
            <div className="flex justify-between pt-4 border-t mt-5">
              <Button variant="outline" onClick={() => setStep(0)}>← Voltar</Button>
              <Button onClick={() => setStep(2)} disabled={!categoria} data-testid="solicitar-step-1-next">
                Continuar →
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: texto + whatsapp */}
        {step === 2 && (
          <div className="space-y-4" data-testid="solicitar-step-2">
            <div className="p-3 rounded-lg bg-purple-50 border border-purple-100 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase font-bold text-purple-700">Categoria</div>
                <div className="font-medium text-sm">{emojiFor(categoria)} {categoria}</div>
              </div>
              <button onClick={() => setStep(1)} className="text-xs text-purple-700 hover:underline">Trocar</button>
            </div>

            <div>
              <Label htmlFor="solicitar-texto" className="mb-1 block">O que você precisa? Seja específico para facilitar o contato.</Label>
              <Textarea
                id="solicitar-texto"
                rows={4}
                maxLength={200}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder={placeholderFor(categoria)}
                data-testid="solicitar-texto-input"
              />
              <div className="text-right text-xs text-gray-500 mt-1" data-testid="solicitar-texto-counter">
                {200 - texto.length} caracteres restantes
              </div>
            </div>

            <div>
              <Label htmlFor="solicitar-wpp" className="mb-1 block">Seu WhatsApp</Label>
              <Input
                id="solicitar-wpp"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                placeholder="Ex: 5511999999999 ou (11) 99999-9999"
                data-testid="solicitar-wpp-input"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Seu número só é compartilhado com os profissionais alertados via link direto. Não fica visível em nenhuma listagem pública.
              </p>
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(1)}>← Voltar</Button>
              <Button
                onClick={() => setStep(3)}
                disabled={!texto.trim() || !whatsapp.trim()}
                data-testid="solicitar-step-2-next"
              >
                Revisar →
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: confirmar */}
        {step === 3 && (
          <div className="space-y-4" data-testid="solicitar-step-3">
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <div className="text-xs uppercase font-bold text-purple-700 mb-1">Resumo</div>
              <p className="text-sm"><strong>{emojiFor(categoria)} {categoria}</strong></p>
              <p className="text-sm text-gray-800 mt-2 whitespace-pre-line">{texto}</p>
              <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> Sua solicitação ficará disponível por <strong>72 horas</strong>.
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Profissionais da categoria receberão alerta em ordem (até 5 por rodada, máx 3 por dia cada).
              </p>
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(2)}>← Editar</Button>
              <Button onClick={enviar} disabled={submitting || limiteAtingido} data-testid="solicitar-enviar-btn">
                {submitting ? 'Enviando…' : 'Enviar solicitação'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
